import { useEffect, useState } from "react";

const STORAGE_KEY_PREFIX = "table_filters_";

function getStoredFilterIds(tableKey: string, defaultIds: string[]): string[] {
  const storageKey = STORAGE_KEY_PREFIX + tableKey;
  const raw = localStorage.getItem(storageKey);

  const uniq = (arr: string[]) => Array.from(new Set(arr));

  if (!raw) {
    const initial = uniq(defaultIds);
    localStorage.setItem(storageKey, JSON.stringify(initial));
    return initial;
  }

  try {
    const parsed = JSON.parse(raw);
    const stored = Array.isArray(parsed) ? parsed : [];
    return uniq(stored);
  } catch {
    const fallback = uniq(defaultIds);
    localStorage.setItem(storageKey, JSON.stringify(fallback));
    return fallback;
  }
}

function setStoredFilterIds(tableKey: string, filterIds: string[]) {
  const storageKey = STORAGE_KEY_PREFIX + tableKey;
  localStorage.setItem(storageKey, JSON.stringify(filterIds));
}

export function usePageFilterIds(
  tableKey: string,
  defaultIds: string[] = [],
  allowedFields?: string[],
) {
  const [filterIds, setFilterIds] = useState<string[]>(() =>
    getStoredFilterIds(tableKey, defaultIds),
  );

  useEffect(() => {
    setStoredFilterIds(tableKey, filterIds);
  }, [tableKey, filterIds]);

  useEffect(() => {
    if (!allowedFields || allowedFields.length === 0) return;

    const storageKey = STORAGE_KEY_PREFIX + tableKey;
    const raw = localStorage.getItem(storageKey);

    try {
      const parsed = JSON.parse(raw || "[]");
      if (Array.isArray(parsed)) {
        const cleaned = parsed.filter((id) => allowedFields.includes(id));
        if (cleaned.length !== parsed.length) {
          localStorage.setItem(storageKey, JSON.stringify(cleaned));
          setFilterIds(cleaned);
        }
      }
    } catch {
      return;
    }
  }, [tableKey, allowedFields]);

  useEffect(() => {
    const next = getStoredFilterIds(tableKey, defaultIds);
    if (JSON.stringify(next) !== JSON.stringify(filterIds)) {
      setFilterIds(next);
    }
  }, [tableKey, defaultIds.join(",")]);

  return [filterIds, setFilterIds] as const;
}
